import React, { useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';

import { LEAVE_STATUS } from '../../../utils/constant/global';
import { LB_HEADER } from '../../../utils/constant/labels';
import { BT_BUTTON_LABELS } from '../../../utils/constant/buttons';

import './AttendanceMetricCardsGrid.scss';

const { ACCEPTED, REJECTED } = LEAVE_STATUS;
const { LB_APPROVE, LB_REJECT, LB_ASK_LEAVE_APPROVE, LB_ASK_LEAVE_REJECT } = LB_HEADER;
const { BTN_ACCEPT, BTN_REJECT, BTN_PLEASE_WAIT } = BT_BUTTON_LABELS;
const initialsFrom = (name = '') => {
  const s = String(name || '').trim();
  if (!s) return '?';
  const parts = s.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase();
  }
  return s.slice(0, 2).toUpperCase();
};

const colorForName = (name = '') => {
  const colors = ['#38bdf8', '#8b5cf6', '#22c55e', '#f59e0b', '#ec4899', '#64748b'];
  const s = String(name || '');
  let h = 0;
  for (let i = 0; i < s.length; i += 1) {
    h = (h + s.charCodeAt(i) * (i + 7)) % colors.length;
  }
  return colors[h];
};

const MetricAvatar = ({ av }) => {
  const src = typeof av === 'string' ? av : av?.src;
  const tooltip = typeof av === 'string' ? null : av?.tooltip || null;
  const displayName = tooltip?.name || (typeof av === 'string' ? '' : av?.name) || '';
  const details = (tooltip?.details || []).filter((row) => row?.label && row?.value);

  const content = src ? (
    <img
      className="attendance-metric-card__avatar"
      src={src}
      alt=""
      loading="lazy"
      decoding="async"
    />
  ) : (
    <span
      className="attendance-metric-card__avatar attendance-metric-card__avatar--initials"
      style={{ background: colorForName(displayName) }}
      aria-hidden
    >
      {initialsFrom(displayName)}
    </span>
  );

  return (
    <span className="attendance-metric-card__avatar-wrap" title={tooltip ? undefined : displayName}>
      {content}
      {tooltip ? (
        <span className="attendance-metric-card__hover-tip" role="tooltip">
          <span className="attendance-metric-card__hover-tip-head">
            <span className="attendance-metric-card__hover-tip-name">
              {tooltip.name || 'Employee'}
            </span>
            {tooltip.designation ? (
              <span className="attendance-metric-card__hover-tip-desig">{tooltip.designation}</span>
            ) : null}
          </span>
          {details.length ? (
            <span className="attendance-metric-card__hover-tip-grid">
              {details.map((row, idx) => (
                <React.Fragment key={`${row.label}-${idx}`}>
                  <span className="attendance-metric-card__hover-tip-label">{row.label}</span>
                  <span className="attendance-metric-card__hover-tip-value">{row.value}</span>
                </React.Fragment>
              ))}
            </span>
          ) : null}
        </span>
      ) : null}
    </span>
  );
};

const renderAvatarStack = (card, onOpen) => {
  const hasPeople = (card.people || []).length > 0;

  const handleActivate = () => {
    if (hasPeople) onOpen(card.id);
  };

  const handleKeyDown = (e) => {
    if (!hasPeople) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onOpen(card.id);
    }
  };

  return (
    <div
      className={`attendance-metric-card__avatars${hasPeople ? '' : ' attendance-metric-card__avatars--empty'}`}
      role="button"
      tabIndex={hasPeople ? 0 : -1}
      onClick={handleActivate}
      onKeyDown={handleKeyDown}
      aria-label={`View ${card.title} employees`}
      aria-disabled={!hasPeople}
      title={hasPeople ? 'Click to view all' : 'No employees'}
    >
      {(card.avatars || []).map((av, i) => (
        <MetricAvatar key={`${card.id}-av-${i}`} av={av} />
      ))}
      {card.extraCount ? (
        <span className="attendance-metric-card__more" aria-label={`${card.extraCount} more`}>
          {card.extraCount}
        </span>
      ) : null}
    </div>
  );
};
const AttendanceMetricCardBody = ({ card, onOpen }) => {
  const showAvatars = (card.avatars || []).length > 0 || Boolean(card.extraCount);

  return (
    <div className="attendance-metric-card__body">
      <div className="attendance-metric-card__value-row">
        <p className="attendance-metric-card__value">{Number(card.value).toLocaleString()}</p>
        <span className="attendance-metric-card__pill">{card.percent}%</span>
      </div>

      <div
        className="attendance-metric-card__bar"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={card.percent}
        aria-label={`${card.percent} percent`}
      >
        <div
          className="attendance-metric-card__bar-fill"
          style={{ width: `${Math.min(100, Math.max(0, card.percent))}%` }}
        />
      </div>

      {showAvatars ? (
        <div className="attendance-metric-card__foot">{renderAvatarStack(card, onOpen)}</div>
      ) : null}
    </div>
  );
};

const TabbedAttendanceMetricCard = ({ card, activeTabId, onTabChange, onOpen }) => {
  const activeTab = card.tabs.find((t) => t.id === activeTabId) || card.tabs[0];
  const tabCount = card.tabs.length;
  const showTabIcons = !card.title;

  return (
    <article
      className={`attendance-metric-card attendance-metric-card--tabbed attendance-metric-card--tabs-${tabCount} attendance-metric-card--${activeTab.theme}`}
    >
      <div
        className={`attendance-metric-card__head attendance-metric-card__head--tabbed${
          card.title ? ' attendance-metric-card__head--tabbed-titled' : ''
        }`}
      >
        {card.title ? <span className="attendance-metric-card__title">{card.title}</span> : null}
        <div
          className="attendance-metric-card__tabs"
          role="tablist"
          aria-label={card.tablistLabel || 'Metric tabs'}
        >
          {card.tabs.map((tab) => {
            const count = Number(tab.value || 0);
            const showCount = Boolean(card.showTabCounts) && count > 0;
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                id={`metric-tab-${tab.id}`}
                aria-selected={activeTabId === tab.id}
                aria-controls={`metric-panel-${tab.id}`}
                aria-label={`${tab.label || tab.title}, ${count}`}
                className={`attendance-metric-card__tab${activeTabId === tab.id ? ' attendance-metric-card__tab--active' : ''}`}
                onClick={() => onTabChange(tab.id)}
              >
                <span className="attendance-metric-card__tab-inner">
                  {showTabIcons && tab.iconClass ? (
                    <i
                      className={`icofont ${tab.iconClass} attendance-metric-card__tab-icon`}
                      aria-hidden
                    />
                  ) : null}
                  <span className="attendance-metric-card__tab-label">
                    {tab.label || tab.title}
                  </span>
                  {showCount ? (
                    <span className="attendance-metric-card__tab-count">{count}</span>
                  ) : null}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div
        id={`metric-panel-${activeTab.id}`}
        role="tabpanel"
        aria-labelledby={`metric-tab-${activeTab.id}`}
        className="attendance-metric-card__panel"
      >
        <AttendanceMetricCardBody card={activeTab} onOpen={onOpen} />
      </div>
    </article>
  );
};

const AttendanceMetricCardsGrid = ({ items = [], onLeaveReview }) => {
  const [openId, setOpenId] = useState(null);
  const [query, setQuery] = useState('');
  const [activeTabByCard, setActiveTabByCard] = useState({});
  const [review, setReview] = useState(null);
  const [reviewReason, setReviewReason] = useState('');
  const [reviewReasonError, setReviewReasonError] = useState('');
  const [reviewing, setReviewing] = useState(false);

  const closeReview = () => {
    setReview(null);
    setReviewReason('');
    setReviewReasonError('');
    setReviewing(false);
  };

  const openReview = (action, person) => {
    if (!person?.leaveId) return;
    setOpenId(null);
    setQuery('');
    setReview({ action, person, leaveId: person.leaveId });
    setReviewReason('');
    setReviewReasonError('');
  };

  const submitReview = async () => {
    const reason = String(reviewReason || '').trim();
    if (!reason) {
      setReviewReasonError('Reason is required');
      return;
    }
    if (reason.length > 140) {
      setReviewReasonError('Reason must be less than 140 characters');
      return;
    }
    if (!onLeaveReview || !review?.leaveId) return;
    setReviewing(true);
    const ok = await onLeaveReview({
      leaveId: review.leaveId,
      status: review.action === 'approve' ? ACCEPTED : REJECTED,
      reason
    });
    setReviewing(false);
    if (ok) closeReview();
  };

  const resolveCard = (id) => {
    for (let i = 0; i < items.length; i += 1) {
      const item = items[i];
      if (item.tabs) {
        const tab = item.tabs.find((t) => t.id === id);
        if (tab) return tab;
      }
      if (item.id === id) return item;
    }
    return null;
  };

  const activeCard = useMemo(() => (openId ? resolveCard(openId) : null), [items, openId]);
  const activePeople = activeCard?.people || [];
  const modalTheme = activeCard?.theme || 'blue';
  const filteredPeople = useMemo(() => {
    const q = String(query || '')
      .trim()
      .toLowerCase();
    if (!q) return activePeople;
    return activePeople.filter((p) => {
      const t = p?.tooltip || {};
      const name = String(t?.name || '').toLowerCase();
      const desig = String(t?.designation || '').toLowerCase();
      return name.includes(q) || desig.includes(q);
    });
  }, [activePeople, query]);

  return (
    <>
      <div className="dashboard-attendance-metric-grid" aria-label="Attendance overview">
        {items.map((card) => {
          if (card.tabs?.length) {
            const activeTabId = activeTabByCard[card.id] || card.tabs[0].id;
            return (
              <TabbedAttendanceMetricCard
                key={card.id}
                card={card}
                activeTabId={activeTabId}
                onTabChange={(tabId) =>
                  setActiveTabByCard((prev) => ({ ...prev, [card.id]: tabId }))
                }
                onOpen={setOpenId}
              />
            );
          }

          return (
            <article
              key={card.id}
              className={`attendance-metric-card attendance-metric-card--${card.theme}`}
            >
              <div className="attendance-metric-card__head">
                <span className="attendance-metric-card__title">{card.title}</span>
                <span className="attendance-metric-card__icon-wrap" aria-hidden>
                  <i className={`icofont ${card.iconClass}`} />
                </span>
              </div>

              <AttendanceMetricCardBody card={card} onOpen={setOpenId} />
            </article>
          );
        })}
      </div>

      <Modal
        show={Boolean(activeCard)}
        onHide={() => {
          setOpenId(null);
          setQuery('');
          closeReview();
        }}
        centered
        scrollable
        size="lg"
        enforceFocus={!review}
        dialogClassName="attendance-people-modal__dialog"
        contentClassName={`attendance-people-modal attendance-people-modal--${modalTheme}`}
        backdropClassName="attendance-people-modal__backdrop"
      >
        <Modal.Header className="attendance-people-modal__head">
          <div className="attendance-people-modal__hero">
            <div className="attendance-people-modal__heroMain">
              <span className="attendance-people-modal__heroIcon" aria-hidden>
                <i className={`icofont ${activeCard?.iconClass || 'icofont-users-alt-5'}`} />
              </span>
              <div className="attendance-people-modal__titleWrap">
                <h2 className="attendance-people-modal__title">
                  {activeCard?.title || 'Employees'}
                </h2>
                <p className="attendance-people-modal__heroSub">Today&apos;s workforce snapshot</p>
              </div>
            </div>
            <button
              type="button"
              className="btn-close attendance-people-modal__close"
              aria-label="Close"
              onClick={() => {
                setOpenId(null);
                setQuery('');
              }}
            />
          </div>

          <div className="attendance-people-modal__stats" aria-label="Summary">
            <div className="attendance-people-modal__stat">
              <span className="attendance-people-modal__statLabel">Total</span>
              <strong className="attendance-people-modal__statValue">{activePeople.length}</strong>
            </div>
            <div className="attendance-people-modal__stat">
              <span className="attendance-people-modal__statLabel">Showing</span>
              <strong className="attendance-people-modal__statValue">
                {filteredPeople.length}
              </strong>
            </div>
            {Number.isFinite(Number(activeCard?.percent)) ? (
              <div className="attendance-people-modal__stat">
                <span className="attendance-people-modal__statLabel">Share</span>
                <strong className="attendance-people-modal__statValue">
                  {activeCard.percent}%
                </strong>
              </div>
            ) : null}
          </div>

          <div className="attendance-people-modal__toolbar">
            <div className="attendance-people-modal__search">
              <i className="bi bi-search" aria-hidden />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="attendance-people-modal__searchInput"
                placeholder="Search name or designation…"
                aria-label="Search employees"
              />
              {query ? (
                <button
                  type="button"
                  className="attendance-people-modal__clear"
                  onClick={() => setQuery('')}
                  aria-label="Clear search"
                >
                  <i className="bi bi-x-lg" aria-hidden />
                </button>
              ) : null}
            </div>
          </div>
        </Modal.Header>

        <Modal.Body className="attendance-people-modal__body">
          {filteredPeople.length ? (
            <>
              <div className="attendance-people-modal__listHead" aria-hidden>
                <span>Employee</span>
                <span>Details</span>
              </div>
              <div className="attendance-people-modal__list" role="list">
                {filteredPeople.map((p, idx) => {
                  const t = p?.tooltip || {};
                  const name = t?.name || 'Employee';
                  const desig = t?.designation || '';
                  const src = p?.src || '';
                  const details = (t?.details || []).filter((d) => d?.value);
                  const checkIn = details.find((d) => d.label === 'Check-in');
                  const checkOut = details.find((d) => d.label === 'Check-out');
                  const otherDetails = details.filter(
                    (d) => d.label !== 'Check-in' && d.label !== 'Check-out'
                  );

                  return (
                    <div
                      // eslint-disable-next-line react/no-array-index-key
                      key={`${activeCard?.id || 'card'}-${t?.name || 'emp'}-${idx}`}
                      className="attendance-people-modal__row"
                      role="listitem"
                      style={{ '--row-index': idx }}
                    >
                      <div className="attendance-people-modal__left">
                        <span className="attendance-people-modal__index">{idx + 1}</span>
                        <span className="attendance-people-modal__avatarShell">
                          {src ? (
                            <img
                              className="attendance-people-modal__avatar"
                              src={src}
                              alt=""
                              loading="lazy"
                              decoding="async"
                            />
                          ) : (
                            <span
                              className="attendance-people-modal__avatar attendance-people-modal__avatar--initials"
                              style={{ background: colorForName(name) }}
                              aria-hidden
                            >
                              {initialsFrom(name)}
                            </span>
                          )}
                          {checkIn?.value ? (
                            <span className="attendance-people-modal__statusDot" aria-hidden />
                          ) : null}
                        </span>

                        <div className="attendance-people-modal__meta">
                          <div className="attendance-people-modal__name">{name}</div>
                          {desig ? (
                            <div className="attendance-people-modal__desig">{desig}</div>
                          ) : null}
                        </div>
                      </div>

                      <div
                        className={`attendance-people-modal__right${
                          onLeaveReview && p?.leaveId && p?.reviewActions?.length
                            ? ' attendance-people-modal__right--inline'
                            : ''
                        }`}
                      >
                        {checkIn?.value ? (
                          <span className="attendance-people-modal__time">
                            <i className="bi bi-clock-fill" aria-hidden />
                            {checkIn.value}
                          </span>
                        ) : null}
                        {checkOut?.value ? (
                          <span className="attendance-people-modal__time attendance-people-modal__time--out">
                            <i className="bi bi-box-arrow-right" aria-hidden />
                            {checkOut.value}
                          </span>
                        ) : null}
                        {otherDetails.length ? (
                          <div className="attendance-people-modal__details">
                            {otherDetails.slice(0, 3).map((d, i) => (
                              <div
                                key={`${d?.label || 'd'}-${i}`}
                                className="attendance-people-modal__chip"
                              >
                                <span className="attendance-people-modal__chipValue">
                                  {d.value}
                                </span>
                              </div>
                            ))}
                          </div>
                        ) : null}
                        {onLeaveReview &&
                        p?.leaveId &&
                        Array.isArray(p?.reviewActions) &&
                        p.reviewActions.length ? (
                          <div className="attendance-people-modal__actions">
                            {p.reviewActions.includes('approve') ? (
                              <button
                                type="button"
                                className="attendance-people-modal__action attendance-people-modal__action--approve"
                                onClick={() => openReview('approve', p)}
                                title="Approve this leave"
                                aria-label="Approve this leave"
                              >
                                <i className="bi bi-check" aria-hidden />
                              </button>
                            ) : null}
                            {p.reviewActions.includes('reject') ? (
                              <button
                                type="button"
                                className="attendance-people-modal__action attendance-people-modal__action--reject"
                                onClick={() => openReview('reject', p)}
                                title={
                                  p?.leaveStatus === ACCEPTED
                                    ? 'Revoke this approved leave'
                                    : 'Reject this leave'
                                }
                                aria-label={
                                  p?.leaveStatus === ACCEPTED
                                    ? 'Revoke this approved leave'
                                    : 'Reject this leave'
                                }
                              >
                                <i className="bi bi-x" aria-hidden />
                              </button>
                            ) : null}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <div className="attendance-people-modal__empty">
              <div className="attendance-people-modal__emptyIcon" aria-hidden>
                <i className="icofont icofont-search-1" />
              </div>
              <div className="attendance-people-modal__emptyTitle">No results</div>
              <div className="attendance-people-modal__emptySub">
                Try a different name or designation.
              </div>
            </div>
          )}
        </Modal.Body>

        {filteredPeople.length ? (
          <Modal.Footer className="attendance-people-modal__foot">
            <span className="attendance-people-modal__footText">
              {filteredPeople.length} of {activePeople.length}{' '}
              {activePeople.length === 1 ? 'employee' : 'employees'}
              {query ? ' matched' : ' listed'}
            </span>
          </Modal.Footer>
        ) : null}
      </Modal>

      <Modal
        centered
        show={Boolean(review)}
        onHide={closeReview}
        className="attendance-leave-review-modal"
        backdrop="static"
      >
        <Modal.Header closeButton>
          <h5 className="modal-title fw-bold">
            {review?.action === 'approve' ? LB_APPROVE : LB_REJECT}
          </h5>
        </Modal.Header>
        <Modal.Body className="justify-content-center flex-column d-flex">
          <i
            className={`display-2 text-center mt-2 ${
              review?.action === 'approve'
                ? 'icofont-simple-smile text-success'
                : 'icofont-sad text-danger'
            }`}
          />
          <p className="mt-4 fs-5 text-center">
            {review?.action === 'approve' ? LB_ASK_LEAVE_APPROVE : LB_ASK_LEAVE_REJECT}
          </p>
          {review?.person?.tooltip?.name ? (
            <p className="text-center text-muted mb-3">{review.person.tooltip.name}</p>
          ) : null}
          <div className="mb-3">
            <textarea
              rows={4}
              maxLength={140}
              className={`form-control mb-3 ${reviewReasonError ? 'is-invalid' : ''}`}
              placeholder="Enter reason"
              value={reviewReason}
              onChange={(e) => {
                setReviewReason(e.target.value);
                setReviewReasonError('');
              }}
            />
            {reviewReasonError ? (
              <div className="invalid-feedback d-block">{reviewReasonError}</div>
            ) : null}
          </div>
          <button
            type="button"
            className={`btn text-white ${review?.action === 'approve' ? 'btn-success' : 'btn-danger'}`}
            disabled={reviewing}
            onClick={submitReview}
          >
            {reviewing ? BTN_PLEASE_WAIT : review?.action === 'approve' ? BTN_ACCEPT : BTN_REJECT}
          </button>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default AttendanceMetricCardsGrid;
